import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Button, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from '@material-ui/core';
import MicIcon from '@material-ui/icons/Mic';
import StopIcon from '@material-ui/icons/Stop';
import ClearIcon from '@material-ui/icons/Clear';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import AudioRecorder from './AudioRecorder';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  dictateBtn: {
    textTransform: 'none',
    fontSize: '0.75rem',
  },
  clearBtn: {
    textTransform: 'none',
    fontSize: '0.75rem',
  },
  recordingIndicator: {
    color: '#ef4444',
    fontSize: '0.6rem',
    marginLeft: theme.spacing(0.5),
    animation: '$pulse 1.5s infinite',
  },
  textareaWrapper: {
    position: 'relative',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
}));

const SpeechTextArea = ({
  label,
  fieldName,
  value,
  onChange,
  onMicClick,
  isListening,
  isLoading,
  activeField,
  interimText,
  placeholder,
  rows = 4,
  disabled = false,
  readOnly = false,
  actionButton = null,
  // Offline recording props
  isOfflineMode = false,
  audioRecordings = [],
  onRecordingComplete,
  onDeleteRecording,
}) => {
  const classes = useStyles();
  const isActive = isListening && activeField === fieldName;
  const isInitializing = isLoading && activeField === fieldName;
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const audioRecorderRef = useRef();
  const [isLocalRecording, setIsLocalRecording] = useState(false);

  // Stop local recording if the user comes back online mid-recording
  useEffect(() => {
    if (!isOfflineMode && isLocalRecording) {
      audioRecorderRef.current?.stopRecording();
    }
  }, [isOfflineMode]); 

  const handleDictateClick = () => {
    if (isOfflineMode) {
      if (isLocalRecording) {
        audioRecorderRef.current?.stopRecording();
        // isLocalRecording resets via handleLocalRecordingComplete
      } else {
        audioRecorderRef.current?.startRecording();
        setIsLocalRecording(true);
      }
    } else {
      onMicClick(fieldName);
    }
  };

  const handleLocalRecordingComplete = (blob, duration) => {
    setIsLocalRecording(false);
    if (onRecordingComplete) onRecordingComplete(fieldName, blob, duration);
  };

  const hasContent = Boolean(value && value.trim());
  const canClear = hasContent && !readOnly && !disabled && !isActive;

  const handleClearClick = () => {
    setConfirmClearOpen(true);
  };

  const handleConfirmClear = () => {
    onChange(fieldName, '');
    setConfirmClearOpen(false);
  };

  const handleCancelClear = () => {
    setConfirmClearOpen(false);
  };

  return (
    <Box>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" className={classes.label}>
            {label}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color={isActive || isLocalRecording ? 'secondary' : 'primary'}
            className={classes.dictateBtn}
            onClick={handleDictateClick}
            disabled={disabled || readOnly || isInitializing}
            startIcon={
              isInitializing
                ? <CircularProgress size={16} color="inherit" />
                : isActive || isLocalRecording
                  ? <StopIcon fontSize="small" />
                  : <MicIcon fontSize="small" />
            }
          >
            {isInitializing ? 'Preparing...' : isLocalRecording ? 'Stop' : isActive ? 'Stop' : isOfflineMode ? 'Record' : 'Dictate'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={classes.clearBtn}
            onClick={handleClearClick}
            disabled={!canClear}
            startIcon={<ClearIcon fontSize="small" />}
          >
            Clear
          </Button>
          {isActive && !isInitializing && (
            <Typography variant="caption" style={{ color: '#ef4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiberManualRecordIcon className={classes.recordingIndicator} />
              Speak now
            </Typography>
          )}
          {audioRecordings && audioRecordings.length > 0 && (
            <Chip
              icon={<AudiotrackIcon />}
              label={`${audioRecordings.length} recording${audioRecordings.length !== 1 ? 's' : ''}`}
              size="medium"
              style={{ 
                backgroundColor: '#fef08a', 
                color: '#000',
                fontWeight: 600,
                fontSize: '0.95rem',
                padding: '16px 8px'
              }}
              title="Saved recordings will be transcribed when online"
            />
          )}
          {actionButton}
        </Box>
      </Box>
      <Box className={classes.textareaWrapper}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={rows}
          value={isActive ? value + (interimText ? ' ' + interimText : '') : value}
          onChange={(e) => onChange(fieldName, e.target.value)}
          placeholder={placeholder}
          InputProps={{
            readOnly: disabled || readOnly || isActive,
            style: isActive ? { borderColor: '#ef4444' } : {},
          }}
        />
        {/* Audio Recorder — controlled by the unified Dictate/Record button above */}
        {(isOfflineMode || audioRecordings.length > 0) && !readOnly && !disabled && (
          <AudioRecorder
            ref={audioRecorderRef}
            onRecordingComplete={handleLocalRecordingComplete}
            disabled={disabled || readOnly}
            recordings={audioRecordings}
            onDeleteRecording={onDeleteRecording}
            hideRecordButton
          />
        )}
      </Box>

      <Dialog open={confirmClearOpen} onClose={handleCancelClear}>
        <DialogTitle>Clear Notes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear this field? Any unsaved content will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClear} color="secondary">
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpeechTextArea;
